import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Type, Search, Plus, Edit, Trash2, CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, CheckSquare,
  Square, LayoutGrid, Layers, FileText, Hash,
  RefreshCcw, Calendar, Shield, RotateCcw, Copy, Tag,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useTranslation } from 'react-i18next';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const MODAL_SHELL = 'bg-white border border-gray-200 backdrop-blur-lg rounded-2xl';
const MODAL_SHADOW = {
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)',
};
const SECTION = 'bg-gray-50 rounded-xl border border-gray-200 overflow-hidden';

const DOC_TYPE_KEYS = {
  facture: 'doc_facture',
  devis: 'doc_devis',
  bc: 'doc_bc',
  bl: 'doc_bl',
  avoir: 'doc_avoir',
};

const RESET_PERIOD_KEYS = {
  annuel: 'reset_annuel',
  mensuel: 'reset_mensuel',
  jamais: 'reset_jamais',
};

export function buildNextNumberPreview(prefix = '', formatPattern = '{NUM}', nextNumber = 1, padding = 4) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const padLen = Math.max(1, parseInt(padding, 10) || 4);
  const num = String(parseInt(nextNumber, 10) || 1).padStart(padLen, '0');

  let formatted = formatPattern || '{NUM}';
  formatted = formatted
    .replace(/\{YYYY\}/g, String(year))
    .replace(/\{YY\}/g, String(year).slice(-2))
    .replace(/\{MM\}/g, month)
    .replace(/\{DD\}/g, day)
    .replace(/\{NUM\}/g, num);

  return `${prefix || ''}${formatted}`;
}

function SequenceEditableCell({
  recordId, field, value, className = 'text-white font-semibold',
  isEditing, editValue, onEditValueChange, onStartEdit, onSave, onCancel, editTitle,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch { /* ignore */ }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) { skipBlurSaveRef.current = false; return; }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlurSaveRef.current = true; onSave(recordId, field, e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }


  const numerotationFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_general_info'), icon: Type, iconColor: 'text-blue-400' },
    { id: 'options', label: p('section_reset_info'), icon: RotateCcw, iconColor: 'text-orange-400' },
  ]), [p]);

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStartEdit(recordId, field, value); } }}
      className={`${className} cursor-pointer`}
      title={editTitle}
    >
      {value || '-'}
    </span>
  );
}

const NumerotationPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.numerotationPage.${key}`, opts);
  const statusFormOptions = useMemo(
    () => [
      { value: 'active', label: p('status_active') },
      { value: 'inactive', label: p('status_inactive') },
    ],
    [p],
  );
  const brandLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const qawaem = isSaudiBrand();
  const saudiBrand = qawaem;
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [sequences, setSequences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [numerotationFormTab, setNumerotationFormTab] = useState('general');
  const [formData, setFormData] = useState({
    sequence_code: '',
    document_type: '',
    prefix: '',
    format_pattern: '',
    next_number: '',
    padding: '',
    reset_period: '',
    last_reset_at: '',
    status: '',
    notes: '',
  });
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_sequences');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isDocTypeFilterOpen, setIsDocTypeFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'تسلسل الترقيم' });

  const getDocTypeLabel = (value) => (value && DOC_TYPE_KEYS[value] ? p(DOC_TYPE_KEYS[value]) : value || '');
  const getResetPeriodLabel = (value) => (value && RESET_PERIOD_KEYS[value] ? p(RESET_PERIOD_KEYS[value]) : value || '');
  const DOCUMENT_TYPE_OPTIONS = [
    { value: 'facture', label: p('doc_facture') },
    { value: 'devis', label: p('doc_devis') },
    { value: 'bc', label: p('doc_bc') },
    { value: 'bl', label: p('doc_bl') },
    { value: 'avoir', label: p('doc_avoir') },
  ];
  const RESET_PERIOD_OPTIONS = [
    { value: 'annuel', label: p('reset_annuel') },
    { value: 'mensuel', label: p('reset_mensuel') },
    { value: 'jamais', label: p('reset_jamais') },
  ];
  const DOCUMENT_TYPE_LABELS = Object.fromEntries(
    Object.entries(DOC_TYPE_KEYS).map(([value, key]) => [value, p(key)])
  );

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const docTypeFilterRef = useRef(null);
  const dateRangeRef = useRef(null);


  const formPreview = useMemo(
    () => buildNextNumberPreview(formData.prefix, formData.format_pattern, formData.next_number || 1, formData.padding || 4),
    [formData.prefix, formData.format_pattern, formData.next_number, formData.padding],
  );

  const viewPreview = useMemo(() => {
    if (!viewingRecord) return '';
    return buildNextNumberPreview(
      viewingRecord.prefix,
      viewingRecord.format_pattern,
      viewingRecord.next_number,
      viewingRecord.padding,
    );
  }, [viewingRecord]);

  const loadSequences = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/numerotation');
      const rows = response?.data || response || [];
      setSequences(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading sequences:', error);
      setSequences([]);
    }
  };

  useEffect(() => { loadSequences(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (docTypeFilterRef.current && !docTypeFilterRef.current.contains(event.target)) setIsDocTypeFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (openStatusDropdown) {
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stats = useMemo(() => {
    const activeSeqs = sequences.filter((s) => s.status === 'active');
    const docTypes = new Set(sequences.map((s) => s.document_type).filter(Boolean)).size;
    const autoCount = sequences.filter((s) => s.reset_period && s.reset_period !== 'jamais').length;
    const primary = activeSeqs[0] || sequences[0];
    const prochainNum = primary
      ? buildNextNumberPreview(primary.prefix, primary.format_pattern, primary.next_number, primary.padding)
      : '—';
    return {
      total: sequences.length,
      active: activeSeqs.length,
      docTypes,
      auto: autoCount,
      prochainNum,
    };
  }, [sequences]);

  const mainTabs = [
    { id: 'all_sequences', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_doc_type'), icon: FileText },
    { id: 'par_reset', name: p('tab_by_reset'), icon: RefreshCcw },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: p('filter_inactive'), icon: XCircle },
    { id: 'auto', name: p('filter_auto'), icon: RotateCcw },
  ];

  const getTypeDistribution = () => DOCUMENT_TYPE_OPTIONS.map((opt) => ({
    id: opt.value,
    label: opt.label,
    count: sequences.filter((s) => s.document_type === opt.value).length,
    color: opt.value === 'facture' ? 'bg-gradient-to-r from-blue-500 to-blue-600'
      : opt.value === 'devis' ? 'bg-gradient-to-r from-purple-500 to-purple-600'
      : opt.value === 'bc' ? 'bg-gradient-to-r from-orange-500 to-orange-600'
      : opt.value === 'bl' ? 'bg-gradient-to-r from-teal-500 to-teal-600'
      : 'bg-gradient-to-r from-red-500 to-red-600',
  }));

  const getResetDistribution = () => RESET_PERIOD_OPTIONS.map((opt) => ({
    id: opt.value,
    label: opt.label,
    count: sequences.filter((s) => s.reset_period === opt.value).length,
    color: opt.value === 'annuel' ? 'bg-gradient-to-r from-green-500 to-green-600'
      : opt.value === 'mensuel' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
      : 'bg-gradient-to-r from-gray-500 to-gray-600',
  }));

  const sequenceDistribution = {
    all_sequences: {
      title: p('dist_overview_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'inactive', label: p('dist_inactive'), count: sequences.filter((s) => s.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { id: 'doc_types', label: p('dist_doc_types'), count: stats.docTypes, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'auto_reset', label: p('dist_auto_reset'), count: stats.auto, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    par_type: { title: p('dist_by_doc_type_title'), data: getTypeDistribution() },
    par_reset: { title: p('dist_by_reset_title'), data: getResetDistribution() },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'inactive', label: p('dist_inactive'), count: sequences.filter((s) => s.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
  };

  const distributionInfo = sequenceDistribution[mainViewMode] || sequenceDistribution.all_sequences;

  const columnFilterRules = useMemo(() => [
    textColumnRule('sequence_code', (row) => row.sequence_code),
    exactColumnRule('document_type', (row) => row.document_type),
    textColumnRule('prefix', (row) => row.prefix),
    textColumnRule('next_number', (row) => String(row.next_number ?? '')),
    exactColumnRule('reset_period', (row) => row.reset_period),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredSequences = useMemo(() => {
    const base = sequences.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (docTypeFilter !== 'all' && s.document_type !== docTypeFilter) return false;
    if (dateRange.start || dateRange.end) {
      const createdDate = new Date(s.created_at);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (createdDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdDate > endOfDay) return false;
      }
    }
    const q = searchTerm.toLowerCase();
    if (q && !s.sequence_code?.toLowerCase().includes(q)
      && !s.prefix?.toLowerCase().includes(q)
      && !s.format_pattern?.toLowerCase().includes(q)
      && !DOCUMENT_TYPE_LABELS[s.document_type]?.toLowerCase().includes(q)) return false;
    if (mainViewMode === 'all_sequences') {
      if (quickFilter === 'active' && s.status !== 'active') return false;
      if (quickFilter === 'inactive' && s.status !== 'inactive') return false;
      if (quickFilter === 'auto' && s.reset_period === 'jamais') return false;
    }
    if (activeDistributionFilter) {
      if (activeDistributionFilter === 'active' && s.status !== 'active') return false;
      if (activeDistributionFilter === 'inactive' && s.status !== 'inactive') return false;
      if (activeDistributionFilter === 'auto_reset' && s.reset_period === 'jamais') return false;
      if (activeDistributionFilter === 'doc_types') return false;
      if (DOCUMENT_TYPE_OPTIONS.some((o) => o.value === activeDistributionFilter) && s.document_type !== activeDistributionFilter) return false;
      if (RESET_PERIOD_OPTIONS.some((o) => o.value === activeDistributionFilter) && s.reset_period !== activeDistributionFilter) return false;
    }
    return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [sequences, statusFilter, docTypeFilter, dateRange, searchTerm, quickFilter, mainViewMode, activeDistributionFilter, filters, columnFilterRules]);



  const resetForm = () => {
    setFormData({
      sequence_code: '', document_type: '', prefix: '', format_pattern: '',
      next_number: '', padding: '', reset_period: '', last_reset_at: '', status: '', notes: '',
    });
    setEditingRecord(null);
    setShowModal(false);
    setNumerotationFormTab('general');
  };

  const openNewModal = () => {
    setFormData(
      saudiBrand
        ? {
            sequence_code: 'SEQ-INV',
            document_type: 'facture',
            prefix: 'INV-',
            format_pattern: '{YYYY}{MM}{NUM}',
            next_number: '1',
            padding: '6',
            reset_period: 'annuel',
            last_reset_at: '',
            status: 'active',
            notes: p('default_sequence_note'),
          }
        : {
            sequence_code: '', document_type: '', prefix: '', format_pattern: '',
            next_number: '', padding: '', reset_period: '', last_reset_at: '', status: '', notes: '',
          },
    );
    setEditingRecord(null);
    setNumerotationFormTab('general');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingRecord) {
        await localApiAdapter.put(`/facturation/numerotation/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/facturation/numerotation', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('notify_save_error'));
    } finally {
      resetForm();
      loadSequences();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      sequence_code: record.sequence_code || '',
      document_type: record.document_type || '',
      prefix: record.prefix || '',
      format_pattern: record.format_pattern || '',
      next_number: record.next_number != null ? String(record.next_number) : '',
      padding: record.padding != null ? String(record.padding) : '',
      reset_period: record.reset_period || '',
      last_reset_at: record.last_reset_at || '',
      status: record.status || '',
      notes: record.notes || '',
    });
    setNumerotationFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/facturation/numerotation/${id}`);
        loadSequences();
        notifyDeleted(showNotify, entityLabel);
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('notify_delete_error'));
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/numerotation/${id}`, { status: newStatus });
      loadSequences();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue != null ? String(currentValue) : '');
  };

  const cancelEditing = () => { setEditingCell(null); setEditValue(''); };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = sequences.find((s) => s.id === recordId);
    if (!record) { cancelEditing(); return; }
    const original = record[field];
    if (String(value) === String(original ?? '') || !String(value).trim()) { cancelEditing(); return; }
    try {
      const payload = field === 'next_number' || field === 'padding'
        ? { [field]: parseInt(value, 10) }
        : { [field]: value };
      await localApiAdapter.put(`/facturation/numerotation/${recordId}`, payload);
      await loadSequences();
      cancelEditing();
      notifyUpdated(showNotify, entityLabel);
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
      notifyError(showNotify, p('notify_inline_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/facturation/numerotation/${id}`)));
        setSelectedRecords([]);
        loadSequences();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredSequences);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredSequences.map((r) => r.id), [filteredSequences]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords(selectedRecords.includes(id)
      ? selectedRecords.filter((rId) => rId !== id)
      : [...selectedRecords, id]);
  };

  const exportHeaders = [
    { key: 'sequence_code', label: p('export_col_sequence_code') },
    { key: 'document_type', label: p('export_col_doc_type') },
    { key: 'prefix', label: p('export_col_prefix') },
    { key: 'format_pattern', label: p('export_col_format') },
    { key: 'next_number', label: p('export_col_next_number') },
    { key: 'padding', label: p('export_col_padding') },
    { key: 'reset_period', label: p('export_col_reset') },
    { key: 'status', label: p('export_col_status') },
    { key: 'notes', label: p('export_col_notes') },
  ];

  const dataToExport = selectedRecords.length > 0
    ? sequences.filter((s) => selectedRecords.includes(s.id))
    : filteredSequences;

  const handleExportCSV = () => {
    exportToCSV(dataToExport, exportHeaders, `numerotation_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    await exportToExcel(dataToExport, exportHeaders, `numerotation_${new Date().toISOString().split('T')[0]}.xlsx`, p('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: p('col_code'), value: (s) => s.sequence_code },
          { label: p('col_doc_type'), value: (s) => DOCUMENT_TYPE_LABELS[s.document_type] || s.document_type },
          { label: p('col_prefix'), value: (s) => s.prefix },
          { label: p('col_format'), value: (s) => s.format_pattern },
          { label: p('export_col_next_short'), value: (s) => buildNextNumberPreview(s.prefix, s.format_pattern, s.next_number, s.padding) },
          { label: p('col_reset'), value: (s) => s.reset_period },
          { label: p('col_status'), value: (s) => s.status },
        ],
        listExportTitle: p('dist_overview_title'),
        clientTitle: p('title'),
        printDocumentTitle: p('export_document_title', { date: new Date().toISOString().split('T')[0] }),
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('error_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const copyPreview = (text) => {
    navigator.clipboard?.writeText(text);
    showNotify(p('copy_success'), { variant: 'success' });
  };

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: Hash, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'docTypes', title: p('stat_doc_types'), value: stats.docTypes, icon: FileText, color: 'from-purple-500 to-purple-600' },
    { key: 'auto', title: p('stat_auto'), value: stats.auto, icon: RotateCcw, color: 'from-orange-500 to-orange-600' },
    { key: 'next', title: p('stat_next_number'), value: stats.prochainNum, icon: Type, color: 'from-teal-500 to-teal-600', className: 'max-lg:hidden' },
  ], [stats, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_active_f') || p('status_values.active') || p('dist_active') },
    { value: 'inactive', label: p('status_inactive_f') || p('status_values.inactive') || p('dist_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? (p('status_label') || p('status_label') || p('col_status'))
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status_label'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'sequence_code', label: p('col_code'), required: true },
    { key: 'document_type', label: p('col_doc_type') },
    { key: 'prefix', label: p('col_prefix') },
    { key: 'next_number', label: p('col_counter') },
    { key: 'reset_period', label: p('col_reset') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 2 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <div className="space-y-3 employees-container">
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />

<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Type}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openNewModal}
      createLabel={p('new_sequence')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
        <div className="space-y-6 pt-6" />
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} isLight={isDashboardLightMode} />

              <div className="relative" ref={docTypeFilterRef}>
                <button onClick={() => setIsDocTypeFilterOpen(!isDocTypeFilterOpen)} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <FileText size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{docTypeFilter === 'all' ? p('doc_type_label') : getDocTypeLabel(docTypeFilter)}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isDocTypeFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDocTypeFilterOpen && (
                  <div className="absolute top-full mt-2 right-0 border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px] z-[99999]" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      {[{ value: 'all', label: p('all_doc_types') }, ...DOCUMENT_TYPE_OPTIONS].map((opt) => (
                        <button key={opt.value} onClick={() => { setDocTypeFilter(opt.value); setIsDocTypeFilterOpen(false); }} className={`w-full text-left px-4 py-2.5 rounded-xl text-white text-sm hover:bg-blue-500/20 ${docTypeFilter === opt.value ? 'bg-blue-500/25' : ''}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={statusFilterRef}>
                <button onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{statusFilter === 'all' ? p('status_label') : statusFilter === 'active' ? p('status_active_f') : p('status_inactive_f')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div className="absolute top-full mt-2 right-0 border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px] z-[99999]" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      {[{ value: 'all', label: p('filter_all_short') }, { value: 'active', label: p('status_active_f') }, { value: 'inactive', label: p('status_inactive_f') }].map((opt) => (
                        <button key={opt.value} onClick={() => { setStatusFilter(opt.value); setIsStatusFilterOpen(false); }} className={`w-full text-left px-4 py-2.5 rounded-xl text-white text-sm hover:bg-blue-500/20 ${statusFilter === opt.value ? 'bg-blue-500/25' : ''}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {selectedRecords.length > 0 && (
                <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300 hover:bg-red-500/30 transition-all">
                  <Trash2 size={16} /><span className="text-sm">{p('delete_count_short', { count: selectedRecords.length })}</span>
                </button>
              )}

              <div className="relative" ref={exportDropdownRef}>
                <button onClick={() => setShowExportDropdown(!showExportDropdown)} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 bg-white/5">
                  <Download size={18} /><span className="text-sm hidden sm:inline">{p('export')}</span><ChevronDown size={14} />
                </button>
                {showExportDropdown && (
                  <div className="absolute top-full mt-2 right-0 border border-white/20 rounded-xl shadow-xl min-w-[180px] z-[99999] bg-slate-800">
                    <button onClick={handleExportCSV} className="w-full flex items-center gap-2 px-4 py-3 text-white hover:bg-white/10 text-sm"><FileOutput size={16} />{p('csv')}</button>
                    <button onClick={handleExportExcel} className="w-full flex items-center gap-2 px-4 py-3 text-white hover:bg-white/10 text-sm"><FileSpreadsheet size={16} />{p('excel')}</button>
                    <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full flex items-center gap-2 px-4 py-3 text-white hover:bg-white/10 text-sm disabled:opacity-50"><FileText size={16} />{isGeneratingPDF ? p('pdf_generating') : p('pdf')}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('sequence_code') ? <TableColumnHeaderFilter columnKey="sequence_code" label={p('col_code')} value={filters.sequence_code || ''} onChange={(v) => setFilter('sequence_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('document_type') ? <TableColumnHeaderFilter columnKey="document_type" label={p('col_doc_type')} kind="select" options={DOCUMENT_TYPE_OPTIONS} value={filters.document_type || ''} onChange={(v) => setFilter('document_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('prefix') ? <TableColumnHeaderFilter columnKey="prefix" label={p('col_prefix')} value={filters.prefix || ''} onChange={(v) => setFilter('prefix', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_format')}</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_preview')}</th>
                  {showCol('next_number') ? <TableColumnHeaderFilter columnKey="next_number" label={p('col_counter')} value={filters.next_number || ''} onChange={(v) => setFilter('next_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('reset_period') ? <TableColumnHeaderFilter columnKey="reset_period" label={p('col_reset')} kind="select" options={RESET_PERIOD_OPTIONS} value={filters.reset_period || ''} onChange={(v) => setFilter('reset_period', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusFormOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredSequences.length > 0 ? pagedItems.map((record, index) => {
                  const preview = buildNextNumberPreview(record.prefix, record.format_pattern, record.next_number, record.padding);
                  return (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      {showCol('sequence_code') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.sequence_code}</span></td>
                      ) : null}
                      {showCol('document_type') ? (
                      <td className="px-6 py-4"><span className="text-white/80 text-sm">{DOCUMENT_TYPE_LABELS[record.document_type] || record.document_type}</span></td>
                      ) : null}
                      {showCol('prefix') ? (
                      <td className="px-6 py-4">
                        <SequenceEditableCell recordId={record.id} field="prefix" value={record.prefix} className="text-white/80 text-sm"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'prefix'} editValue={editValue}
                          onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          editTitle={p('click_to_edit')} />
                      </td>
                      ) : null}
                      <td className="px-6 py-4"><span className="text-white/60 text-sm font-mono">{record.format_pattern}</span></td>
                      <td className="px-6 py-4">
                        <span className="text-emerald-300 font-mono font-semibold text-sm">{preview}</span>
                      </td>
                      {showCol('next_number') ? (
                      <td className="px-6 py-4"><span className="text-white font-bold">{record.next_number}</span></td>
                      ) : null}
                      {showCol('reset_period') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{getResetPeriodLabel(record.reset_period)}</span></td>
                      ) : null}
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view') || p('view') || t('common.view')}
                          editTitle={p('action_edit') || p('edit') || t('common.edit')}
                          deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                        />
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><Type size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_sequences_total', { count: filteredSequences.length })}</span>
                      <span>{p('footer_active', { count: filteredSequences.filter((s) => s.status === 'active').length })}</span>
                      <span>{p('footer_auto_reset', { count: filteredSequences.filter((s) => s.reset_period !== 'jamais').length })}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredSequences.length > 0 ? pagedItems.map((record, index) => {
              const preview = buildNextNumberPreview(record.prefix, record.format_pattern, record.next_number, record.padding);
              return (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Type size={20} className="text-blue-400" />{record.sequence_code}</h3>
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('card_type')}</span> {DOCUMENT_TYPE_LABELS[record.document_type]}</div>
                          <div><span className="font-medium">{p('card_prefix')}</span> {record.prefix || '—'}</div>
                          <div><span className="font-medium">{p('card_format')}</span> <span className="font-mono">{record.format_pattern}</span></div>
                        </div>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('card_counter')}</span> {record.next_number}</div>
                          <div><span className="font-medium">{p('card_padding')}</span> {record.padding}</div>
                          <div><span className="font-medium">{p('card_reset')}</span> {getResetPeriodLabel(record.reset_period)}</div>
                        </div>
                        <div className="p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                          <span className="font-medium text-emerald-300 text-xs">{p('card_next_number')}</span>
                          <p className="text-emerald-200 font-mono font-bold text-lg mt-1">{preview}</p>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              );
            }) : (
              <div className="col-span-full text-center py-12"><Type size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('view_title')}
          icon={Type}
          maxWidth="900px"
          testId="numerotation-view-modal"
        >
              <div className={`${SECTION} p-5 mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-gray-500 text-sm mb-1">{p('live_preview_next')}</p>
                    <p className="text-2xl font-bold font-mono text-emerald-700">{viewPreview}</p>
                    <p className="text-gray-400 text-xs mt-2">{p('live_preview_formula', { prefix: viewingRecord.prefix || '', pattern: viewingRecord.format_pattern, counter: viewingRecord.next_number, padding: viewingRecord.padding })}</p>
                  </div>
                  <button onClick={() => copyPreview(viewPreview)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm">
                    <Copy size={16} />{p('copy')}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className={`${SECTION} p-5`}>
                  <h3 className="text-gray-900 font-semibold mb-4">{p('section_general')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-gray-500 text-sm mb-1">{p('label_sequence_code')}</p><p className="text-gray-900 font-medium">{viewingRecord.sequence_code}</p></div>
                    <div><p className="text-gray-500 text-sm mb-1">{p('label_doc_type')}</p><p className="text-gray-900 font-medium">{DOCUMENT_TYPE_LABELS[viewingRecord.document_type] || viewingRecord.document_type}</p></div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">{p('label_status')}</p>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${viewingRecord.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {viewingRecord.status === 'active' ? p('status_active') : p('status_inactive')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`${SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4">{p('section_format_counter')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-gray-500 text-sm mb-1">{p('col_prefix')}</p><p className="text-gray-900 font-medium font-mono">{viewingRecord.prefix || '—'}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_format_pattern')}</p><p className="text-gray-900 font-medium font-mono">{viewingRecord.format_pattern}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_next_counter')}</p><p className="text-gray-900 font-bold text-xl">{viewingRecord.next_number}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_padding')}</p><p className="text-gray-900 font-medium">{p('label_padding_digits', { count: viewingRecord.padding })}</p></div>
                    </div>
                  </div>
                  <div className={`${SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2"><RefreshCcw size={18} className="text-blue-500" />{p('section_reset')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_reset_period')}</p><p className="text-gray-900 font-medium">{getResetPeriodLabel(viewingRecord.reset_period)}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('last_reset_label')}</p><p className="text-gray-900 font-medium">{viewingRecord.last_reset_at ? new Date(viewingRecord.last_reset_at).toLocaleString(brandLocale) : '—'}</p></div>
                      {viewingRecord.notes && <div><p className="text-gray-500 text-sm mb-1">{p('label_notes')}</p><p className="text-gray-900 font-medium">{viewingRecord.notes}</p></div>}
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('edit_title') : p('create_title')}
          icon={Type}
          formId="numerotation-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          onSubmit
          maxWidth="1000px"
          testId="numerotation-crud-modal"
          tabs={numerotationFormTabs}
          activeTab={numerotationFormTab}
          onTabChange={setNumerotationFormTab}
        >
              <div className="mb-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                <p className="text-emerald-300 text-xs mb-1">{p('live_preview')}</p>
                <p className="text-emerald-200 font-mono font-bold text-xl">{formPreview}</p>
              </div>

              <form id="numerotation-form" onSubmit={handleSubmit}>
                {numerotationFormTab === 'general' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_sequence_code')}</label>
                            <input type="text" value={formData.sequence_code} onChange={(e) => setFormData({ ...formData, sequence_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_sequence_code')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_doc_type')}</label>
                            <CustomSelect value={formData.document_type} onChange={(v) => setFormData({ ...formData, document_type: v })} options={DOCUMENT_TYPE_OPTIONS} placeholder={p('placeholder_select')} searchable autoSelect={false} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                            <CustomSelect value={formData.status} onChange={(v) => setFormData({ ...formData, status: v })} options={statusFormOptions} placeholder={p('placeholder_status')} searchable autoSelect={false} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_prefix')}</label>
                            <input type="text" value={formData.prefix} onChange={(e) => setFormData({ ...formData, prefix: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={saudiBrand ? p('placeholder_prefix_inv') : p('placeholder_prefix')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_format_pattern')}</label>
                            <input type="text" value={formData.format_pattern} onChange={(e) => setFormData({ ...formData, format_pattern: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={saudiBrand ? p('placeholder_format_inv') : p('placeholder_format')} />
                            <p className="text-white/40 text-xs mt-1">{p('format_tokens')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_next_number_form')}</label>
                            <CustomNumberInput step={1} value={formData.next_number} onChange={(v) => setFormData({ ...formData, next_number: v })} placeholder={p('placeholder_next_number')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_padding_form')}</label>
                            <CustomNumberInput step={1} value={formData.padding} onChange={(v) => setFormData({ ...formData, padding: v })} placeholder={p('placeholder_padding')} />
                          </div>
                        </div>
                )}

                {numerotationFormTab === 'options' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reset_period_form')}</label>
                            <CustomSelect value={formData.reset_period} onChange={(v) => setFormData({ ...formData, reset_period: v })} options={RESET_PERIOD_OPTIONS} placeholder={p('placeholder_period')} searchable autoSelect={false} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_last_reset')}</label>
                            <DatePicker
                              selected={formData.last_reset_at ? new Date(formData.last_reset_at) : null}
                              onChange={(date) => setFormData({ ...formData, last_reset_at: date ? date.toISOString() : '' })}
                              locale={datePickerLocale}
                              dateFormat={saudiBrand ? 'yyyy/MM/dd' : 'dd/MM/yyyy'}
                              placeholderText={p('optional_date')}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              wrapperClassName="w-full"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes_info')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}
    </div>
  );
};

export default NumerotationPage;

